// AI assisted development
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
} from '@mui/material';
import { Visibility as ViewIcon, Edit as EditIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { useState } from 'react';
import api from '@/services/api';
import { Release } from '@/types';

export default function History() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const successMessage = (location.state as { message?: string } | null)?.message;

  const { data: releases, isLoading, error } = useQuery({
    queryKey: ['releases'],
    queryFn: () => api.getReleases(),
  });

  const handleViewDetails = async (release: Release) => {
    setSelectedRelease(release);
    setDialogOpen(true);
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Error loading releases</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        Release History
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Production date</TableCell>
              <TableCell>Published</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Edits after publish</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(releases) && releases.map((release) => {
              const totalItems = (Array.isArray(release.itemGroups) ? release.itemGroups : []).reduce(
                (sum, group) => sum + (Array.isArray(group.items) ? group.items.length : 0), 0
              );

              return (
                <TableRow key={release.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {release.title}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(release.releaseDate)}</TableCell>
                  <TableCell>
                    {release.publishedAt ? formatDateTime(release.publishedAt) : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip label={totalItems} size="small" />
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        release.lastPostPublishUpdatedAt
                          ? `Last: ${formatDateTime(release.lastPostPublishUpdatedAt)}`
                          : 'No updates since initial publish'
                      }
                    >
                      <Chip
                        label={release.postPublishUpdateCount ?? 0}
                        size="small"
                        variant={(release.postPublishUpdateCount ?? 0) > 0 ? 'filled' : 'outlined'}
                        color={(release.postPublishUpdateCount ?? 0) > 0 ? 'secondary' : 'default'}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => handleViewDetails(release)}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => navigate(`/releases/${release.id}/edit`)}
                      sx={{ ml: 1 }}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {(!Array.isArray(releases) || releases.length === 0) && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No releases yet</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedRelease?.title}</DialogTitle>
        <DialogContent dividers>
          {selectedRelease && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Description
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
                {selectedRelease.description}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Production date
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {formatDate(selectedRelease.releaseDate)}
              </Typography>

              {selectedRelease.externalId && (
                <>
                  <Typography variant="subtitle2" color="text.secondary">
                    External ID
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    {selectedRelease.externalId}
                  </Typography>
                </>
              )}

              <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                Items
              </Typography>
              
              {Array.isArray(selectedRelease.itemGroups) && selectedRelease.itemGroups.map((group) => {
                // Group items by module
                const itemsByModule = (Array.isArray(group.items) ? group.items : []).reduce((acc, item) => {
                  const mod = item.module || 'General';
                  if (!acc[mod]) acc[mod] = [];
                  acc[mod].push(item);
                  return acc;
                }, {} as Record<string, typeof group.items>);

                return (
                  <Paper key={group.productSuite} sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
                      {group.productSuite}
                    </Typography>
                    {Object.entries(itemsByModule).map(([moduleName, items]) => (
                      <Box key={moduleName} sx={{ mb: 2, ml: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                          {moduleName}
                        </Typography>
                        {items.map((item, idx) => (
                          <Box key={item.itemId || idx} sx={{ mb: 1, ml: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.issueKey} - {item.summary}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                              {item.content}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Paper>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          {selectedRelease && (
            <Button 
              variant="contained" 
              startIcon={<EditIcon />}
              onClick={() => {
                setDialogOpen(false);
                navigate(`/releases/${selectedRelease.id}/edit`);
              }}
            >
              Edit Release
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
